import React, { createContext, useContext, useEffect, useState } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { absoluteUrl } from '@/lib/navigation';

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  signUp: (email: string, password: string, name: string, workspaceName: string, trialConfirmed?: boolean) => Promise<{ error: Error | null }>;
  /** Sign up without creating a workspace (for invite flow). Backend must set raw_user_meta_data.invite_signup = true. */
  signUpInviteOnly: (email: string, password: string, name: string) => Promise<{ error: Error | null }>;
  signIn: (email: string, password: string) => Promise<{ error: Error | null }>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Environment variables are validated by EnvErrorBoundary in main.tsx
    // If we reach here, env vars are valid

    let mounted = true;
    let subscription: { unsubscribe: () => void } | null = null;

    // Wrap everything in try-catch to ensure AuthProvider always provides context
    try {
      // Set up auth state listener FIRST
      let authSubscriptionResult;
      try {
        authSubscriptionResult = supabase.auth.onAuthStateChange(
          (event, session) => {
            if (!mounted) return;
            if (import.meta.env.DEV) {
              console.log('[AuthProvider] Auth state changed:', event, session?.user?.id || 'no user');
            }
            setSession(session);
            setUser(session?.user ?? null);
            setLoading(false);
          }
        );
      } catch (error) {
        console.error('[AuthProvider] Error calling onAuthStateChange:', error);
        // If Supabase client is not initialized, set loading to false and continue
        setLoading(false);
        return;
      }
      
      const { data: { subscription: authSubscription }, error: listenerError } = authSubscriptionResult || { data: { subscription: null }, error: null };
      
      if (listenerError) {
        console.error('[AuthProvider] Error setting up auth listener:', listenerError);
        setLoading(false);
        return;
      }
      
      if (authSubscription) {
        subscription = authSubscription;
      }

      // THEN check for existing session with timeout safeguard
      let sessionPromise;
      try {
        sessionPromise = supabase.auth.getSession();
      } catch (error) {
        console.error('[AuthProvider] Error calling getSession:', error);
        setLoading(false);
        return;
      }

      const timeoutPromise = new Promise<{ data: { session: null }, error: Error }>((resolve) => {
        setTimeout(() => {
          resolve({
            data: { session: null },
            error: new Error('Session check timeout')
          });
        }, 10000); // 10 second timeout
      });

      Promise.race([sessionPromise, timeoutPromise]).then((result) => {
        if (!mounted) return;
        
        const { data: { session }, error } = result || { data: { session: null }, error: new Error('Unknown error') };
        
        if (error) {
          console.error('[AuthProvider] Error getting session:', error);
          // Don't block app on session check errors - set loading to false
          setLoading(false);
        } else {
          setSession(session);
          setUser(session?.user ?? null);
          setLoading(false);
        }
      }).catch((err) => {
        if (!mounted) return;
        console.error('[AuthProvider] Unexpected error in session check:', err);
        setLoading(false);
      });
    } catch (error) {
      // Catch any synchronous errors during initialization (e.g., supabase client not initialized)
      console.error('[AuthProvider] Failed to initialize auth:', error);
      // Always set loading to false so app can continue
      setLoading(false);
    }

    return () => {
      mounted = false;
      if (subscription) {
        try {
          subscription.unsubscribe();
        } catch (error) {
          console.error('[AuthProvider] Error unsubscribing:', error);
        }
      }
    };
  }, []);

  const signUp = async (email: string, password: string, name: string, workspaceName: string, trialConfirmed: boolean = true) => {
    try {
      const redirectUrl = absoluteUrl('/auth/callback');
      const { error: authError } = await supabase.auth.signUp({
        email,
        password,
        options: {
          emailRedirectTo: redirectUrl,
          data: { name, workspace_name: workspaceName, trial_confirmed: trialConfirmed }
        }
      });
      if (authError) throw authError;
      return { error: null };
    } catch (error) {
      return { error: error as Error };
    }
  };

  const signUpInviteOnly = async (email: string, password: string, name: string) => {
    try {
      const redirectUrl = absoluteUrl('/auth/callback');
      const { error: authError } = await supabase.auth.signUp({
        email,
        password,
        options: {
          emailRedirectTo: redirectUrl,
          data: { name, invite_signup: true }
        }
      });
      if (authError) throw authError;
      return { error: null };
    } catch (error) {
      return { error: error as Error };
    }
  };

  const signIn = async (email: string, password: string) => {
    try {
      const { error } = await supabase.auth.signInWithPassword({
        email,
        password
      });
      if (error) throw error;
      return { error: null };
    } catch (error) {
      return { error: error as Error };
    }
  };

  const signOut = async () => {
    await supabase.auth.signOut();
    setUser(null);
    setSession(null);
  };

  return (
    <AuthContext.Provider value={{ user, session, loading, signUp, signUpInviteOnly, signIn, signOut }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
